import React, { useMemo, useState } from "react";
import {
  BarChart3,
  TrendingUp,
  FileText,
  Users,
  CheckCircle2,
  Clock3,
  XCircle,
  Download,
  CalendarDays,
} from "lucide-react";
import "../styles/Reports.css";

const monthlyData = [
  { month: "Apr", quotes: 42, completed: 24 },
  { month: "May", quotes: 58, completed: 31 },
  { month: "Jun", quotes: 71, completed: 39 },
  { month: "Jul", quotes: 64, completed: 35 },
  { month: "Aug", quotes: 86, completed: 51 },
  { month: "Sep", quotes: 98, completed: 62 },
];

const statusData = [
  {
    label: "New",
    value: 28,
    color: "new",
  },
  {
    label: "Contacted",
    value: 21,
    color: "contacted",
  },
  {
    label: "Quotation Sent",
    value: 18,
    color: "quotation",
  },
  {
    label: "In Progress",
    value: 14,
    color: "progress",
  },
  {
    label: "Completed",
    value: 32,
    color: "completed",
  },
  {
    label: "Cancelled",
    value: 7,
    color: "cancelled",
  },
];

const moveTypeData = [
  {
    name: "Residential Move",
    count: 54,
    percentage: 48,
  },
  {
    name: "Office Move",
    count: 26,
    percentage: 23,
  },
  {
    name: "Vehicle Transportation",
    count: 19,
    percentage: 17,
  },
  {
    name: "Commercial Move",
    count: 14,
    percentage: 12,
  },
];

const recentReports = [
  {
    id: "Q-1028",
    customer: "Arun Kumar",
    route: "Erode → Chennai",
    date: "28 Sep 2026",
    amount: "₹32,500",
    status: "Completed",
  },
  {
    id: "Q-1027",
    customer: "Priya S",
    route: "Coimbatore → Bangalore",
    date: "27 Sep 2026",
    amount: "₹28,000",
    status: "In Progress",
  },
  {
    id: "Q-1026",
    customer: "Karthik R",
    route: "Chennai → Madurai",
    date: "26 Sep 2026",
    amount: "₹24,500",
    status: "Quotation Sent",
  },
  {
    id: "Q-1025",
    customer: "Meena Devi",
    route: "Salem → Erode",
    date: "25 Sep 2026",
    amount: "₹16,800",
    status: "Completed",
  },
  {
    id: "Q-1024",
    customer: "Suresh B",
    route: "Tiruppur → Chennai",
    date: "24 Sep 2026",
    amount: "₹35,000",
    status: "Contacted",
  },
];

export default function Reports() {
  const [period, setPeriod] = useState("6 Months");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const totalQuotes = useMemo(
    () => monthlyData.reduce((sum, item) => sum + item.quotes, 0),
    []
  );

  const completedQuotes = useMemo(
    () => monthlyData.reduce((sum, item) => sum + item.completed, 0),
    []
  );

  const totalRevenue = 1847500;

  const maxQuotes = Math.max(
    ...monthlyData.map((item) => item.quotes)
  );

  const getStatusClass = (status) => {
    switch (status) {
      case "Completed":
        return "report-status completed";
      case "In Progress":
        return "report-status progress";
      case "Quotation Sent":
        return "report-status quotation";
      case "Contacted":
        return "report-status contacted";
      default:
        return "report-status";
    }
  };

  const handleExport = () => {
    alert("Report export will be connected to the backend.");
  };

  return (
    <div className="reports-page">

      {/* Header */}
      <div className="reports-header">
        <div className="reports-title">
          <div className="reports-title-icon">
            <BarChart3 size={22} />
          </div>

          <div>
            <h1>Reports</h1>
            <p>
              Analyze quotes, customers and business performance
            </p>
          </div>
        </div>

        <button
          className="report-export-btn"
          onClick={handleExport}
        >
          <Download size={17} />
          Export Report
        </button>
      </div>

      {/* Filters */}
      <div className="reports-filter-card">

        <div className="report-filter-item">
          <label>Report Period</label>

          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option>Today</option>
            <option>This Week</option>
            <option>This Month</option>
            <option>3 Months</option>
            <option>6 Months</option>
            <option>This Year</option>
            <option>Custom</option>
          </select>
        </div>

        <div className="report-filter-item">
          <label>From Date</label>

          <div className="date-input">
            <CalendarDays size={16} />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
        </div>

        <div className="report-filter-item">
          <label>To Date</label>

          <div className="date-input">
            <CalendarDays size={16} />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        <button className="apply-report-btn">
          Apply Filter
        </button>

      </div>

      {/* Summary Cards */}
      <div className="report-summary-grid">

        <div className="report-summary-card">
          <div className="report-card-icon blue">
            <FileText size={21} />
          </div>

          <div>
            <span>Total Quotes</span>
            <strong>{totalQuotes}</strong>
            <small>
              <TrendingUp size={13} />
              12.5% from previous period
            </small>
          </div>
        </div>

        <div className="report-summary-card">
          <div className="report-card-icon green">
            <CheckCircle2 size={21} />
          </div>

          <div>
            <span>Completed Quotes</span>
            <strong>{completedQuotes}</strong>
            <small>
              <TrendingUp size={13} />
              8.2% from previous period
            </small>
          </div>
        </div>

        <div className="report-summary-card">
          <div className="report-card-icon orange">
            <Clock3 size={21} />
          </div>

          <div>
            <span>Pending Quotes</span>
            <strong>61</strong>
            <small className="neutral-text">
              Currently pending
            </small>
          </div>
        </div>

        <div className="report-summary-card">
          <div className="report-card-icon purple">
            <Users size={21} />
          </div>

          <div>
            <span>Total Customers</span>
            <strong>486</strong>
            <small>
              <TrendingUp size={13} />
              15.4% growth
            </small>
          </div>
        </div>

      </div>

      {/* Revenue Card */}
      <div className="revenue-card">

        <div className="revenue-left">
          <div className="revenue-icon">
            <TrendingUp size={21} />
          </div>

          <div>
            <span>Total Quotation Value</span>
            <strong>
              ₹{totalRevenue.toLocaleString("en-IN")}
            </strong>
            <small>
              Based on selected reporting period
            </small>
          </div>
        </div>

        <div className="revenue-right">
          <span>Completion Rate</span>
          <strong>63.3%</strong>

          <div className="completion-bar">
            <div style={{ width: "63.3%" }} />
          </div>
        </div>

      </div>

      {/* Charts */}
      <div className="reports-chart-grid">

        {/* Monthly Quotes */}
        <div className="report-chart-card">

          <div className="report-card-header">
            <div>
              <h3>Quote Overview</h3>
              <p>Monthly quote activity</p>
            </div>

            <span className="chart-period">
              {period}
            </span>
          </div>

          <div className="bar-chart">

            {monthlyData.map((item) => (
              <div
                className="bar-column"
                key={item.month}
              >

                <div className="bar-value">
                  {item.quotes}
                </div>

                <div className="bar-wrapper">

                  <div
                    className="bar quotes-bar"
                    style={{
                      height: `${
                        (item.quotes / maxQuotes) * 100
                      }%`,
                    }}
                  />

                  <div
                    className="bar completed-bar"
                    style={{
                      height: `${
                        (item.completed / maxQuotes) *
                        100
                      }%`,
                    }}
                  />

                </div>

                <span>{item.month}</span>

              </div>
            ))}

          </div>

          <div className="chart-legend">
            <span>
              <i className="legend-dot quotes-dot" />
              Total Quotes
            </span>

            <span>
              <i className="legend-dot completed-dot" />
              Completed
            </span>
          </div>

        </div>

        {/* Status */}
        <div className="report-chart-card">

          <div className="report-card-header">
            <div>
              <h3>Quote Status</h3>
              <p>Current quote distribution</p>
            </div>
          </div>

          <div className="status-report-list">

            {statusData.map((item) => (
              <div
                className="status-report-item"
                key={item.label}
              >

                <div className="status-report-info">
                  <span
                    className={`status-color ${item.color}`}
                  />

                  <span>{item.label}</span>

                  <strong>{item.value}</strong>
                </div>

                <div className="status-progress">
                  <div
                    className={`status-progress-fill ${item.color}`}
                    style={{
                      width: `${item.value * 2.3}%`,
                    }}
                  />
                </div>

              </div>
            ))}

          </div>

        </div>

      </div>

      {/* Move Type + Recent Quotes */}
      <div className="reports-bottom-grid">

        {/* Move Types */}
        <div className="report-chart-card">

          <div className="report-card-header">
            <div>
              <h3>Move Type Distribution</h3>
              <p>Quotes by moving category</p>
            </div>
          </div>

          <div className="move-type-list">

            {moveTypeData.map((item) => (
              <div
                className="move-type-item"
                key={item.name}
              >

                <div className="move-type-header">
                  <span>{item.name}</span>
                  <strong>{item.count}</strong>
                </div>

                <div className="move-type-bar">
                  <div
                    style={{
                      width: `${item.percentage}%`,
                    }}
                  />
                </div>

                <small>
                  {item.percentage}% of total quotes
                </small>

              </div>
            ))}

          </div>

        </div>

        {/* Recent Quotes */}
        <div className="report-chart-card recent-report-card">

          <div className="report-card-header">
            <div>
              <h3>Recent Quotes</h3>
              <p>Latest quotation activity</p>
            </div>
          </div>

          <div className="recent-report-list">

            {recentReports.map((item) => (
              <div
                className="recent-report-item"
                key={item.id}
              >

                <div className="recent-report-main">

                  <strong>{item.id}</strong>

                  <span>{item.customer}</span>

                  <small>{item.route}</small>

                </div>

                <div className="recent-report-side">

                  <strong>{item.amount}</strong>

                  <span className={getStatusClass(item.status)}>
                    {item.status}
                  </span>

                </div>

              </div>
            ))}

          </div>

        </div>

      </div>

    </div>
  );
}